import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useCallback } from "react";
import { useApi } from "@/core/api";
import { invalidateCustomerQueries } from "@/customer/query";
import { referralsKey } from "@/referrals/query";
import { ReferralSource } from "./referral-source";

const referralSourcesKey = (businessId?: string | null) =>
  ["referral-sources", businessId] as const;

export const useGetReferralSources = <TData = ReferralSource[]>(
  businessId?: string | null,
  select?: (data: ReferralSource[]) => TData,
) => {
  const { get } = useApi();

  return useQuery({
    queryKey: referralSourcesKey(businessId),
    queryFn: async () => {
      const params = new URLSearchParams({ business_id: businessId || "" });

      return get("/marketing_referral_sources", { params }).then(
        (res) => res.data,
      );
    },
    enabled: !!businessId,
    select,
  });
};

export const useGetReferralSource = (
  businessId?: string | null,
  referralSourceId?: string | null,
) =>
  useGetReferralSources(
    businessId,
    useCallback(
      (sources: ReferralSource[]) =>
        sources.find((source) => source.id === referralSourceId) || null,
      [referralSourceId],
    ),
  );

export const useUpsertReferralSource = (businessId: string) => {
  const { put, post } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: Partial<ReferralSource>) =>
      params.id
        ? put(`/marketing_referral_sources/${params.id}`, params)
        : post("/marketing_referral_sources", {
            ...params,
            business_id: businessId,
          }),
    onSuccess: (response, origParams) => {
      const newRecord = response.data;
      const queryKey = referralSourcesKey(businessId);

      queryClient.setQueryData<ReferralSource[]>(queryKey, (oldData) => {
        if (!oldData) return oldData;

        if (origParams.id) {
          return oldData.map((oldRecord) =>
            oldRecord.id === origParams.id
              ? { ...oldRecord, ...newRecord }
              : oldRecord,
          );
        } else {
          return [...oldData, newRecord];
        }
      });

      invalidateCustomerQueries(queryClient, businessId);
      queryClient.invalidateQueries({ queryKey: referralsKey(businessId) });
    },
  });
};
